// frontend_app/src/lib/searchStatus.js
//
// Per-lab status of a streaming search. A lab (display group) can receive
// several packages (Sonic has seven brands, PanelApp two), so its status
// settles only once every expected package has arrived.

const countDisplayable = (pkg) =>
  (pkg.results || []).filter((r) => r && r.is_displayable !== false).length;

/**
 * `packages`: packages received so far; `expected`: { group: package count };
 * `finished`: the stream has ended (done, stopped or failed).
 * Returns { group: { status, results, received, expected, truncated, messages } }
 * where status is pending | searching | success | no_match | error | unavailable | stopped.
 */
export const summariseLabs = (packages, expected = {}, finished = false) => {
  const byGroup = {};
  packages.forEach((pkg) => {
    const group = pkg.display_group || pkg.source_name;
    (byGroup[group] = byGroup[group] || []).push(pkg);
  });

  const summary = {};
  new Set([...Object.keys(expected), ...Object.keys(byGroup)]).forEach((group) => {
    const pkgs = byGroup[group] || [];
    const want = Math.max(expected[group] || 0, pkgs.length);
    const results = pkgs.reduce((n, p) => n + countDisplayable(p), 0);
    const errors = pkgs.filter((p) => p.status === 'error');
    const unavailable = pkgs.filter((p) => p.status === 'unavailable');
    const complete = pkgs.length >= want;

    let status;
    if (!complete && !finished) status = pkgs.length ? 'searching' : 'pending';
    else if (results > 0) status = 'success';
    else if (errors.length) status = 'error';
    else if (unavailable.length) status = 'unavailable';
    else if (!complete) status = 'stopped';
    else status = 'no_match';

    summary[group] = {
      status,
      results,
      received: pkgs.length,
      expected: want,
      truncated: pkgs.some((p) => (p.total_matches || 0) > (p.results || []).length),
      totalMatches: pkgs.reduce((n, p) => n + (p.total_matches ?? countDisplayable(p)), 0),
      messages: [...errors, ...unavailable]
        .map((p) => p.error_message)
        .filter(Boolean)
        .filter((m, i, all) => all.indexOf(m) === i),
    };
  });
  return summary;
};

export const isSettled = (status) => !['pending', 'searching'].includes(status);
